import { css, createGlobalStyle } from 'styled-components';

// =============================================================================
// COLOR TOKENS
// =============================================================================
export const colors = {
  // Primary
  vert: '#d5ff4f',
  bleu: '#595fe7',
  bleuFonce: '#314959',
  orange: '#ff8b3d',
  bleuClair: '#83a7c3',

  // Pastels
  bleuPastel: '#ddedff',
  vertPastel: '#e9ffcd',
  orangePastel: '#ffdfcf',
  violetPastel: '#ede1ff',
  grisClair: '#f4f4f4',
  darkSection: '#31353f',

  // Text
  textDark: '#314959',
  textSecondary: '#667085',
  borderLight: '#d3dde6',
  white: '#ffffff',

  // Status
  statusOpen: '#c1f7d8',
  statusOpenText: '#065f46',
  statusClosedTemp: '#fef3cd',
  statusClosedTempText: '#92400e',
  statusClosedDef: '#fde8e8',
  statusClosedDefText: '#991b1b',
};

// =============================================================================
// SERVICE CATEGORY COLORS
// =============================================================================
export const serviceCategoryColors = {
  vente: {
    bg: colors.bleuPastel,
    text: colors.bleuFonce,
  },
  atelier: {
    bg: colors.violetPastel,
    text: '#5b21b6',
  },
  extra: {
    bg: colors.orangePastel,
    text: '#9a3412',
  },
};

// Service name to category mapping
export const SERVICE_CATEGORIES = {
  'Vente vélos neufs': 'vente',
  'Vente vélos occasion': 'vente',
  'Vente accessoires': 'vente',
  'Vente pièces détachées': 'vente',
  'Vente vélos électriques': 'vente',
  'Réparation vélo': 'atelier',
  'Entretien/révision': 'atelier',
  'Réparation vélo électrique': 'atelier',
  'Montage vélo': 'atelier',
  'Location vélo': 'extra',
  'Location VAE': 'extra',
  'Gravage vélo': 'extra',
  'Conseils personnalisés': 'extra',
  'Atelier participatif': 'extra',
  Livraison: 'extra',
};

export function getServiceCategory(serviceName) {
  return SERVICE_CATEGORIES[serviceName] || 'extra';
}

// =============================================================================
// TYPOGRAPHY
// =============================================================================
export const fonts = {
  heading: "'Aron', sans-serif",
  body:
    "'LT Saeada', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

// =============================================================================
// SPACING & SIZING
// =============================================================================
export const spacing = {
  containerMaxWidth: '1060px',
  containerPadding: '24px',
  containerPaddingDesktop: '32px',
  sectionPadding: '48px',
  sectionPaddingDesktop: '64px',
  cardGap: '16px',
  cardGapLarge: '24px',
};

export const radii = {
  card: '20px',
  pill: '50px',
  small: '12px',
  button: '50px',
};

export const shadows = {
  card: '1px 1px 10px 1px rgba(0,0,0,0.1)',
  none: 'none',
};

// =============================================================================
// BREAKPOINTS
// =============================================================================
export const breakpoints = {
  mobile: '768px',
};

// =============================================================================
// STATUS BADGE CONFIG
// =============================================================================
export function getStatusStyle(statut) {
  switch (statut) {
    case 'Ouvert':
      return { bg: colors.statusOpen, color: colors.statusOpenText };
    case 'Fermé':
    case 'Fermé temporairement':
      return {
        bg: colors.statusClosedTemp,
        color: colors.statusClosedTempText,
      };
    case 'Fermé définitivement':
      return { bg: colors.statusClosedDef, color: colors.statusClosedDefText };
    default:
      return { bg: colors.grisClair, color: colors.textSecondary };
  }
}

// =============================================================================
// CSS HELPERS
// =============================================================================
export const containerStyle = css`
  max-width: ${spacing.containerMaxWidth};
  margin: 0 auto;
  padding: 0 ${spacing.containerPadding};

  @media (min-width: ${breakpoints.mobile}) {
    padding: 0 ${spacing.containerPaddingDesktop};
  }
`;

export const sectionPaddingStyle = css`
  padding: ${spacing.sectionPadding} 0;

  @media (min-width: ${breakpoints.mobile}) {
    padding: ${spacing.sectionPaddingDesktop} 0;
  }
`;

export const headingStyle = css`
  font-family: ${fonts.heading};
  font-weight: 700;
  line-height: 1.2;
  color: ${colors.textDark};
`;

export const bodyTextStyle = css`
  font-family: ${fonts.body};
  font-weight: 400;
  line-height: 1.65;
  color: ${colors.textDark};
  font-size: 16px;
`;

// =============================================================================
// GLOBAL FONT-FACE STYLES (inject once at app level)
// =============================================================================
export const AnnuaireFonts = createGlobalStyle`
  @font-face {
    font-family: 'Aron';
    src: url('/static/fonts/annuaire/AronVFVF-BF6556dd3e535eb.woff2') format('woff2'),
         url('/static/fonts/annuaire/AronVFVF-BF6556dd3e535eb.ttf') format('truetype');
    font-weight: 100 900;
    font-display: swap;
  }

  @font-face {
    font-family: 'LT Saeada';
    src: url('/static/fonts/annuaire/LTSaeada-Light.woff') format('woff');
    font-weight: 300;
    font-display: swap;
  }

  @font-face {
    font-family: 'LT Saeada';
    src: url('/static/fonts/annuaire/LTSaeada-Regular.woff') format('woff');
    font-weight: 400;
    font-display: swap;
  }

  @font-face {
    font-family: 'LT Saeada';
    src: url('/static/fonts/annuaire/LTSaeada-Medium.woff') format('woff');
    font-weight: 500;
    font-display: swap;
  }

  @font-face {
    font-family: 'LT Saeada';
    src: url('/static/fonts/annuaire/LTSaeada-SemiBold.woff') format('woff');
    font-weight: 600;
    font-display: swap;
  }

  @font-face {
    font-family: 'LT Saeada';
    src: url('/static/fonts/annuaire/LTSaeada-Bold.woff') format('woff');
    font-weight: 700;
    font-display: swap;
  }
`;

// =============================================================================
// ICON PATHS (RoulezJeunesse branded illustrations)
// =============================================================================
const ICON_BASE = '/static/img/annuaire/icones';

export const icones = {
  localisation: `${ICON_BASE}/ROULEZJEUNESSE_ICONES__LOCALISATION.png`,
  smartphone: `${ICON_BASE}/ROULEZJEUNESSE_ICONES__SMARTPHONE.png`,
  velo: `${ICON_BASE}/ROULEZJEUNESSE_ICONES__VELO.png`,
  pouceOk: `${ICON_BASE}/ROULEZJEUNESSE_ICONES__POUCE_OK.png`,
  shop: `${ICON_BASE}/ROULEZJEUNESSE_ICONES__SHOP.png`,
  calendrier: `${ICON_BASE}/ROULEZJEUNESSE_ICONES__CALENDRIER.png`,
  checklist: `${ICON_BASE}/ROULEZJEUNESSE_ICONES__CHECKLIST.png`,
  aide: `${ICON_BASE}/ROULEZJEUNESSE_ICONES__AIDE.png`,
  infos: `${ICON_BASE}/ROULEZJEUNESSE_ICONES__INFOS.png`,
  reparationMain: `${ICON_BASE}/ROULEZJEUNESSE_ICONES__REPARATION_MAIN.png`,
  outils: `${ICON_BASE}/ROULEZJEUNESSE_ICONES__OUTILS.png`,
};

// =============================================================================
// UTILITY: Parse FAQ from "Q1: ... R1: ..." format
// =============================================================================
export function parseFaq(faqText) {
  if (!faqText) return [];
  const regex = /Q(\d+):\s*(.+?)\nR\1:\s*(.+?)(?=\n\nQ|$)/gs;
  const faqs = [];
  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(faqText)) !== null) {
    faqs.push({ question: match[2].trim(), reponse: match[3].trim() });
  }
  return faqs;
}

const DAYS_EN = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];
const DAYS_FR_LOWER = [
  'dimanche',
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
];

/**
 * Convert a single time like "2:00 PM" → "14:00".
 */
function timeTo24h(h, m, period) {
  let hour = parseInt(h, 10);
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  return `${hour.toString().padStart(2, '0')}:${m}`;
}

/**
 * Convert a time range like "10:00 AM – 12:00 PM" or "2:00 – 7:00 PM" to 24h.
 * When the start time has no AM/PM, it inherits from the end time.
 */
function convertRangeTo24h(rangeStr) {
  const parts = rangeStr.trim().split(/\s*[–\-—]\s*/);
  if (parts.length !== 2) return rangeStr;
  const timeRe = /(\d{1,2}):(\d{2})\s*(AM|PM)?/i;
  const startMatch = parts[0].match(timeRe);
  const endMatch = parts[1].match(timeRe);
  if (!startMatch || !endMatch) return rangeStr;
  const endPeriod = endMatch[3] ? endMatch[3].toUpperCase() : null;
  const startPeriod = startMatch[3] ? startMatch[3].toUpperCase() : endPeriod;
  if (!endPeriod) return rangeStr; // no AM/PM at all → already 24h
  return `${timeTo24h(startMatch[1], startMatch[2], startPeriod)}–${timeTo24h(
    endMatch[1],
    endMatch[2],
    endPeriod,
  )}`;
}

/**
 * Translate common English schedule terms to French + convert AM/PM to 24h.
 */
function translateHours(text) {
  let result = text
    .replace(/\bClosed\b/gi, 'Fermé')
    .replace(/\bOpen 24 hours\b/gi, 'Ouvert 24h');
  // Convert AM/PM time ranges to 24h format
  if (/\d{1,2}:\d{2}\s*(AM|PM)/i.test(result)) {
    result = result.split(',').map(convertRangeTo24h).join(', ');
  }
  return result;
}

// =============================================================================
// UTILITY: Get today's hours from horaires
// =============================================================================
export function getHorairesAujourdHui(horaires) {
  if (!horaires || !horaires.weekdayDescriptions) return null;
  const dayIndex = new Date().getDay();
  const todayFr = DAYS_FR_LOWER[dayIndex];
  const todayEn = DAYS_EN[dayIndex];
  const desc = horaires.weekdayDescriptions.find((d) => {
    const lower = d.toLowerCase();
    return lower.startsWith(todayFr) || lower.startsWith(todayEn);
  });
  if (!desc) return null;
  const parts = desc.split(':').slice(1).join(':').trim();
  return translateHours(parts) || null;
}

// =============================================================================
// UTILITY: Check if shop is currently open (based on horaires)
// =============================================================================
export function isShopOpen(horaires) {
  if (!horaires) return false;
  if (typeof horaires.openNow === 'boolean') return horaires.openNow;
  return false;
}

export function isOpenNowFromHoraires(horaires) {
  const todayHours = getHorairesAujourdHui(horaires);
  if (!todayHours) return null; // unknown
  const lower = todayHours.toLowerCase();
  if (lower === 'fermé' || lower === 'closed') return false;
  if (lower === 'ouvert 24h' || lower === 'open 24 hours') return true;

  // Parse time ranges like "09:00–12:00, 14:00–19:00"
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  // Match ranges with various dash characters (–, -, —)
  const rangeRegex = /(\d{1,2}):(\d{2})\s*[–\-—]\s*(\d{1,2}):(\d{2})/g;
  let rangeMatch;
  let foundRange = false;
  // eslint-disable-next-line no-cond-assign
  while ((rangeMatch = rangeRegex.exec(todayHours)) !== null) {
    foundRange = true;
    const startMin =
      parseInt(rangeMatch[1], 10) * 60 + parseInt(rangeMatch[2], 10);
    const endMin =
      parseInt(rangeMatch[3], 10) * 60 + parseInt(rangeMatch[4], 10);
    if (currentMinutes >= startMin && currentMinutes < endMin) return true;
  }
  // If we found ranges but none matched, shop is closed now
  if (foundRange) return false;
  // Could not parse — unknown
  return null;
}
